// filepath: apps/web/src/features/directory/organization-tree/OrganizationTreeNode.tsx
//
// FR-UI-DIR-1 AC-3 — recursive node in the organization unit tree.
// ISS-517 — per-node Create-Child / Edit / Move / Archive actions.
//
// DEPENDENCY DIRECTION. This module is under `features/` and imports
// ONLY from `features/directory/*` and `@/design-system/*`. It must
// NOT import from `apps/web/src/admin/organization-units/` — the
// dependency edge runs admin -> features, never the reverse (see
// 02-impact-analysis.md § "Package Dependencies", the flagged
// direction-of-deps row). The move dialog is therefore built here
// from the feature-layer `useMoveOrganizationUnit` hook plus native
// `<select>` + design-system `<Button>`, rather than reusing the
// admin `<OrganizationUnitMoveForm>`.
//
// INV-2: only server-emitted values are rendered. The wire carries
// `legal_entity_id` — NEVER `legal_entity_name`. The parent page
// resolves ids to names through its own legal-entity map and passes
// the label down; a node with no matching legal entity renders
// nothing rather than guessing.
//
// ISS-517 D1: the five phantom fields (`legal_entity_name`,
// `headcount`, `unit_kind`, `member_count`, `updated_at`) and the
// pre-rename `parent_id` are GONE from the wire type. Rendering them
// was the silent-empty bug this MR removes.
//
// BA ruling D2: the Move dialog performs NO client-side cycle check.
// Selecting an illegal parent is answered by the server with 409 +
// `code: "cycle_detected"`, mapped here to the picker error slot.

import React, { useCallback, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Button } from "@/design-system/Button";
import { Badge } from "@/design-system/Badge";
import { navigate } from "@/features/cabinet/usePathname";
import {
  useArchiveOrganizationUnit,
  useMoveOrganizationUnit,
} from "../queries";
import type { OrganizationUnit } from "../types";

export interface OrganizationTreeNodeProps {
  unit: OrganizationUnit;
  childrenByParent: ReadonlyMap<string, readonly OrganizationUnit[]>;
  /** id -> display name, resolved by the page from the legal-entity feed. */
  legalEntityNames?: ReadonlyMap<string, string>;
  /** id -> display name, resolved by the page from the unit list. */
  unitNames?: ReadonlyMap<string, string>;
  /** Called when the node's Archive action succeeds. */
  onArchived?: () => void;
}

/**
 * Recursive tree node. Depth is bounded by the height of the
 * organization tree (typically < 10 levels in practice); React
 * handles recursion natively and there is no iterative flattening.
 */
export function OrganizationTreeNode({
  unit,
  childrenByParent,
  legalEntityNames,
  unitNames,
  onArchived,
}: OrganizationTreeNodeProps): React.ReactElement {
  const { t } = useTranslation();
  const children = childrenByParent.get(unit.id) ?? [];
  const moveMutation = useMoveOrganizationUnit();
  const archiveMutation = useArchiveOrganizationUnit();
  const [moveOpen, setMoveOpen] = useState<boolean>(false);
  const [nextParentId, setNextParentId] = useState<string>(
    unit.parent_organization_unit_id ?? "",
  );
  const [moveError, setMoveError] = useState<string | null>(null);

  const legalEntityName = legalEntityNames?.get(unit.legal_entity_id) ?? null;
  const isClosed = unit.status === "closed";

  const onMove = useCallback(async () => {
    setMoveError(null);
    try {
      await moveMutation.mutateAsync({
        id: unit.id,
        parent_organization_unit_id: nextParentId || null,
        row_version: unit.row_version,
      });
      setMoveOpen(false);
    } catch (e) {
      // BA ruling D2 — the server's recursive CTE is the ONLY cycle
      // authority. A 409 + code='cycle_detected' is mapped to the
      // picker error slot; a 409 + row_version_mismatch falls back to
      // the server message so a reload prompt is available.
      if (
        typeof e === "object" &&
        e !== null &&
        (e as { code?: string }).code === "cycle_detected"
      ) {
        setMoveError(
          t(
            "directory.organization_units.move.parent_cycle",
            "Reparent would introduce a cycle.",
          ),
        );
      } else {
        setMoveError(e instanceof Error ? e.message : String(e));
      }
    }
  }, [moveMutation, unit.id, unit.row_version, nextParentId, t]);

  const onArchive = useCallback(async () => {
    setMoveError(null);
    try {
      await archiveMutation.mutateAsync({
        id: unit.id,
        row_version: unit.row_version,
      });
      onArchived?.();
    } catch (e) {
      setMoveError(e instanceof Error ? e.message : String(e));
    }
  }, [archiveMutation, unit.id, unit.row_version, onArchived]);

  // The move picker offers every unit EXCEPT this one. No descendant
  // filtering is performed client-side — an illegal choice is
  // answered by the server, which is the single authority.
  const parentCandidates = unitNames
    ? [...unitNames.entries()]
        .filter(([id]) => id !== unit.id)
        .map(([id, name]) => ({ id, name }))
    : [];

  return (
    <li
      className="directory-org__node"
      data-testid={`org-node-${unit.id}`}
      data-unit-id={unit.id}
    >
      <div className="directory-org__node-label">
        <span className="directory-org__node-name">{unit.name}</span>
        {unit.code ? (
          <span className="directory-org__node-code">{unit.code}</span>
        ) : null}
        {/* INV-2: the name comes from the legal-entity FEED, which the
            server emitted. Nothing is joined or synthesised here. */}
        {legalEntityName ? (
          <span className="directory-org__node-entity">{legalEntityName}</span>
        ) : null}
        {isClosed ? (
          <Badge tone="neutral" data-testid={`org-node-status-${unit.id}`}>
            {t("directory.organization_units.status.closed", "Closed")}
          </Badge>
        ) : null}
      </div>

      <div className="directory-org__node-actions">
        {/* design-adherence-ignore: <Button> test hook via wrapper. */}
        <span data-testid={`org-node-add-child-${unit.id}`}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate(
                `/admin/organization-units/new?parent=${encodeURIComponent(unit.id)}`,
              )
            }
          >
            {t("directory.organization_units.action.add_child", "Add child")}
          </Button>
        </span>
        <span data-testid={`org-node-edit-${unit.id}`}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate(
                `/admin/organization-units/${encodeURIComponent(unit.id)}/edit`,
              )
            }
          >
            {t("directory.organization_units.action.edit", "Edit")}
          </Button>
        </span>
        <span data-testid={`org-node-move-${unit.id}`}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setNextParentId(unit.parent_organization_unit_id ?? "");
              setMoveError(null);
              setMoveOpen((v) => !v);
            }}
          >
            {t("directory.organization_units.action.move", "Move")}
          </Button>
        </span>
        {!isClosed ? (
          <span data-testid={`org-node-archive-${unit.id}`}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void onArchive()}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending
                ? t(
                    "directory.organization_units.action.archiving",
                    "Archiving…",
                  )
                : t("directory.organization_units.action.archive", "Archive")}
            </Button>
          </span>
        ) : null}
      </div>

      {moveOpen ? (
        <div
          className="directory-org__node-move"
          data-testid={`org-node-move-panel-${unit.id}`}
        >
          <label htmlFor={`org-node-move-parent-${unit.id}`}>
            {t("directory.organization_units.move.parent_label", "New parent")}
          </label>
          <select
            id={`org-node-move-parent-${unit.id}`}
            value={nextParentId}
            onChange={(e) => setNextParentId(e.target.value)}
            aria-invalid={moveError ? true : undefined}
            data-testid={`org-node-move-parent-select-${unit.id}`}
          >
            <option value="">
              {t(
                "directory.organization_units.form.no_parent",
                "(no parent — root)",
              )}
            </option>
            {parentCandidates.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {moveError ? (
            <p role="alert" data-testid={`org-node-move-error-${unit.id}`}>
              {moveError}
            </p>
          ) : null}
          {/* design-adherence-ignore: <Button> test hook via wrapper. */}
          <span data-testid={`org-node-move-submit-${unit.id}`}>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void onMove()}
              disabled={moveMutation.isPending}
            >
              {moveMutation.isPending
                ? t("directory.organization_units.action.moving", "Moving…")
                : t("directory.organization_units.move.submit", "Move")}
            </Button>
          </span>
        </div>
      ) : null}

      {/* Children render even when their parent is closed: the Go
          `Close` does NOT cascade (`repository.go:206-222`), so hiding
          them here would misrepresent the stored data. */}
      {children.length > 0 ? (
        <ul className="directory-org__children">
          {children.map((child) => (
            <OrganizationTreeNode
              key={child.id}
              unit={child}
              childrenByParent={childrenByParent}
              legalEntityNames={legalEntityNames}
              unitNames={unitNames}
              onArchived={onArchived}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
